import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Send, MessageSquare, Calendar, Package, Scissors, Info, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import NoticeBanner from '@/components/NoticeBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REQUEST_TYPES = [
  { value: 'consulta', label: 'Consulta General', icon: MessageSquare, desc: 'Cualquier duda sobre nuestros productos o servicios' },
  { value: 'cita_telefonica', label: 'Agendar Cita Telefonica', icon: Phone, desc: 'Indica tramo horario de contacto (L-J)' },
  { value: 'problema_pedido', label: 'Problemas con mi Pedido', icon: Package, desc: 'Incidencias, retrasos o consultas sobre tu pedido' },
  { value: 'protesis_semipersonalizada', label: 'Solicitar Protesis Semipersonalizada', icon: Scissors, desc: 'Indica las caracteristicas de tu pieza ideal' },
  { value: 'info_general', label: 'Informacion General', icon: Info, desc: 'Informacion sobre la tienda, envios, devoluciones...' },
  { value: 'cita_fisica_madrid', label: 'Solicitar Cita Fisica en Madrid', icon: MapPin, desc: 'Visita nuestra sede en Gran Via 46, Madrid' }
];

const DAYS = [
  { value: 'lunes', label: 'Lunes' },
  { value: 'martes', label: 'Martes' },
  { value: 'miercoles', label: 'Miercoles' },
  { value: 'jueves', label: 'Jueves' }
];

export default function Contact({ user }) {
  const [requestType, setRequestType] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    message: '',
    preferred_days: [],
    preferred_time_slot: '',
    order_reference: '',
    hair_color: '',
    hair_density: '',
    hair_texture: '',
    base_size: '',
    grey_percentage: '',
    additional_specs: '',
    preferred_date: ''
  });

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day)
        ? prev.preferred_days.filter(d => d !== day)
        : [...prev.preferred_days, day]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!requestType) {
      toast.error('Selecciona un tipo de solicitud');
      return;
    }
    setSending(true);
    try {
      const payload = {
        request_type: requestType,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || null
      };

      if (requestType === 'cita_telefonica') {
        if (formData.preferred_days.length === 0) {
          toast.error('Selecciona al menos un dia preferido');
          setSending(false);
          return;
        }
        payload.preferred_days = formData.preferred_days;
        payload.preferred_time_slot = formData.preferred_time_slot;
      } else if (requestType === 'problema_pedido') {
        payload.order_reference = formData.order_reference;
      } else if (requestType === 'protesis_semipersonalizada') {
        payload.hair_color = formData.hair_color;
        payload.hair_density = formData.hair_density;
        payload.hair_texture = formData.hair_texture;
        payload.base_type = formData.base_type;
        payload.base_size = formData.base_size;
        payload.grey_percentage = formData.grey_percentage;
        payload.additional_specs = formData.additional_specs;
      } else if (requestType === 'cita_fisica_madrid') {
        payload.preferred_date = formData.preferred_date;
      }

      const response = await fetch(`${BACKEND_URL}/api/contact/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSent(true);
        toast.success('Solicitud enviada correctamente');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error al enviar');
      }
    } catch (error) {
      toast.error('Error de conexion');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-white">
        <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
          <div className="container mx-auto px-6 py-4">
            <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ARTHUR FITZ</Link>
          </div>
        </header>
        <div className="container mx-auto px-6 py-20 max-w-lg text-center" data-testid="contact-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Send className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>Solicitud Enviada</h1>
          <p className="text-gray-600 mb-2">Hemos recibido tu solicitud correctamente.</p>
          <p className="text-gray-600 mb-8">Nuestro equipo se pondra en contacto contigo lo antes posible.</p>
          <div className="space-y-3">
            <Button onClick={() => { setSent(false); setRequestType(''); }} variant="outline" className="w-full border-2 border-black">
              Enviar otra solicitud
            </Button>
            <Link to="/">
              <Button className="w-full bg-black text-white mt-2">Volver a la tienda</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ARTHUR FITZ</Link>
        </div>
      </header>

      <NoticeBanner />

      <div className="container mx-auto px-6 py-12 max-w-5xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Playfair Display' }}>Contacto Personalizado</h1>
          <p className="text-gray-600">Selecciona el motivo de tu consulta y te atenderemos de forma personalizada</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: WhatsApp + Type selector */}
          <div className="lg:col-span-1 space-y-6">
            <div className="border-2 border-black p-6 bg-gray-50">
              <h3 className="font-bold text-lg mb-3">Contacto Directo</h3>
              <p className="text-gray-600 text-sm mb-4">Atencion inmediata por WhatsApp</p>
              <Button
                data-testid="whatsapp-btn"
                onClick={() => window.open('https://wa.me/34649471889', '_blank')}
                className="w-full bg-black text-white hover:bg-gray-800"
              >
                <Phone className="w-5 h-5 mr-2" />
                649 471 889
              </Button>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-3">Tipo de solicitud</h3>
              <div className="space-y-2">
                {REQUEST_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      data-testid={`type-${type.value}`}
                      onClick={() => setRequestType(type.value)}
                      className={`w-full text-left p-3 border-2 transition-all ${
                        requestType === type.value
                          ? 'border-black bg-black text-white'
                          : 'border-gray-200 hover:border-black'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm">{type.label}</p>
                          <p className={`text-xs mt-0.5 ${requestType === type.value ? 'text-gray-300' : 'text-gray-500'}`}>{type.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-2">
            {!requestType ? (
              <div className="border-2 border-dashed border-gray-300 p-12 text-center h-full flex items-center justify-center">
                <div>
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 text-lg">Selecciona un tipo de solicitud para comenzar</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="border-2 border-black p-6 space-y-5" data-testid="contact-form">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
                  {REQUEST_TYPES.find(t => t.value === requestType)?.label}
                </h2>

                {/* Common fields */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre completo *</Label>
                    <Input data-testid="contact-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Telefono *</Label>
                    <Input data-testid="contact-phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="border-gray-300" />
                  </div>
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input data-testid="contact-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="border-gray-300" />
                </div>

                {/* Cita telefonica fields */}
                {requestType === 'cita_telefonica' && (
                  <div className="space-y-4 border-t pt-4">
                    <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                      Las citas telefonicas solo estan disponibles de <strong>Lunes a Jueves</strong>.
                    </div>
                    <div>
                      <Label>Dias preferidos *</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {DAYS.map((day) => (
                          <button
                            key={day.value}
                            type="button"
                            data-testid={`day-${day.value}`}
                            onClick={() => toggleDay(day.value)}
                            className={`px-4 py-2 border-2 text-sm font-medium transition-all ${
                              formData.preferred_days.includes(day.value)
                                ? 'border-black bg-black text-white'
                                : 'border-gray-300 hover:border-black'
                            }`}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Franja horaria preferida</Label>
                      <select
                        data-testid="time-slot-select"
                        className="w-full border-2 border-gray-300 rounded p-2 mt-1"
                        value={formData.preferred_time_slot}
                        onChange={(e) => setFormData({ ...formData, preferred_time_slot: e.target.value })}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="09:00 - 12:00">09:00 - 12:00 (Mañana)</option>
                        <option value="12:00 - 15:00">12:00 - 15:00 (Mediodia)</option>
                        <option value="15:00 - 18:00">15:00 - 18:00 (Tarde)</option>
                        <option value="18:00 - 20:00">18:00 - 20:00 (Ultima hora)</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Problema pedido fields */}
                {requestType === 'problema_pedido' && (
                  <div className="border-t pt-4">
                    <Label>Referencia o numero de pedido</Label>
                    <Input
                      data-testid="order-ref-input"
                      value={formData.order_reference}
                      onChange={(e) => setFormData({ ...formData, order_reference: e.target.value })}
                      placeholder="Ej: 32e21998 o email con el que hiciste el pedido"
                      className="border-gray-300"
                    />
                  </div>
                )}

                {/* Protesis semipersonalizada fields */}
                {requestType === 'protesis_semipersonalizada' && (
                  <div className="space-y-4 border-t pt-4">
                    <p className="text-sm text-gray-600">Indica las caracteristicas de la pieza que necesitas. Consultaremos stock y te contactaremos para comentar disponibilidad.</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Color del cabello</Label>
                        <Input data-testid="hair-color" value={formData.hair_color} onChange={(e) => setFormData({ ...formData, hair_color: e.target.value })} placeholder="Ej: Castaño oscuro, #1B" className="border-gray-300" />
                      </div>
                      <div>
                        <Label>Densidad</Label>
                        <select
                          data-testid="hair-density"
                          className="w-full border-2 border-gray-300 rounded p-2 mt-1"
                          value={formData.hair_density}
                          onChange={(e) => setFormData({ ...formData, hair_density: e.target.value })}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="Ligera">Ligera</option>
                          <option value="Media-Ligera">Media-Ligera</option>
                          <option value="Media">Media</option>
                          <option value="Media-Alta">Media-Alta</option>
                          <option value="Alta">Alta</option>
                        </select>
                      </div>
                      <div>
                        <Label>Textura</Label>
                        <select
                          data-testid="hair-texture"
                          className="w-full border-2 border-gray-300 rounded p-2 mt-1"
                          value={formData.hair_texture}
                          onChange={(e) => setFormData({ ...formData, hair_texture: e.target.value })}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="Liso">Liso</option>
                          <option value="Ondulado ligero">Ondulado ligero</option>
                          <option value="Ondulado">Ondulado</option>
                          <option value="Rizado">Rizado</option>
                        </select>
                      </div>
                      <div>
                        <Label>Tipo de base</Label>
                        <select
                          data-testid="base-type"
                          className="w-full border-2 border-gray-300 rounded p-2 mt-1"
                          value={formData.base_type || ''}
                          onChange={(e) => setFormData({ ...formData, base_type: e.target.value })}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="Malla Francesa">Malla Francesa</option>
                          <option value="Malla Suiza">Malla Suiza</option>
                          <option value="Skin anudada fina">Skin anudada fina</option>
                          <option value="Skin anudada media">Skin anudada media</option>
                          <option value="Skin anudada fuerte">Skin anudada fuerte</option>
                          <option value="Skin V-Loop fina">Skin V-Loop fina</option>
                          <option value="Frontal malla + skin">Frontal malla + skin</option>
                          <option value="Malla francesa con laterales y trasera en skin">Malla francesa con laterales y trasera en skin</option>
                          <option value="No estoy seguro">No estoy seguro</option>
                        </select>
                      </div>
                      <div>
                        <Label>Tamano de la base</Label>
                        <Input data-testid="base-size" value={formData.base_size} onChange={(e) => setFormData({ ...formData, base_size: e.target.value })} placeholder="Ej: 8x10 pulgadas" className="border-gray-300" />
                      </div>
                      <div>
                        <Label>Porcentaje de canas</Label>
                        <select
                          data-testid="grey-pct"
                          className="w-full border-2 border-gray-300 rounded p-2 mt-1"
                          value={formData.grey_percentage}
                          onChange={(e) => setFormData({ ...formData, grey_percentage: e.target.value })}
                        >
                          <option value="">Seleccionar...</option>
                          <option value="0%">0% (Sin canas)</option>
                          <option value="5%">5%</option>
                          <option value="10%">10%</option>
                          <option value="15%">15%</option>
                          <option value="20%">20%</option>
                          <option value="30%">30%</option>
                          <option value="40%">40%</option>
                          <option value="50%">50%</option>
                          <option value="60%">60%</option>
                          <option value="65%">65%</option>
                          <option value="70%">70%</option>
                          <option value="80%">80%</option>
                          <option value="100%">100%</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label>Especificaciones adicionales</Label>
                      <Textarea
                        data-testid="additional-specs"
                        value={formData.additional_specs}
                        onChange={(e) => setFormData({ ...formData, additional_specs: e.target.value })}
                        rows={3}
                        placeholder="Cualquier detalle extra sobre la pieza que necesitas..."
                        className="border-gray-300"
                      />
                    </div>
                  </div>
                )}

                {/* Cita fisica Madrid fields */}
                {requestType === 'cita_fisica_madrid' && (
                  <div className="space-y-4 border-t pt-4">
                    <div className="bg-gray-50 border border-gray-200 p-3 text-sm">
                      <strong>Ubicacion:</strong> Gran Via 46, 9A - 28013 Madrid
                    </div>
                    <div>
                      <Label>Fecha preferida</Label>
                      <Input
                        data-testid="preferred-date"
                        type="date"
                        value={formData.preferred_date}
                        onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className="border-gray-300"
                      />
                    </div>
                  </div>
                )}

                {/* Message (always visible) */}
                <div>
                  <Label>{requestType === 'problema_pedido' ? 'Describe el problema *' : 'Mensaje adicional'}</Label>
                  <Textarea
                    data-testid="contact-message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    placeholder="Escribe aqui tu consulta..."
                    className="border-gray-300"
                    required={requestType === 'problema_pedido'}
                  />
                </div>

                <Button
                  data-testid="submit-contact-btn"
                  type="submit"
                  className="w-full bg-black text-white hover:bg-gray-800 py-6 text-base font-medium"
                  disabled={sending}
                >
                  {sending ? 'Enviando...' : 'ENVIAR SOLICITUD'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
